<template>
  <v-card variant="outlined" class="mt-2">
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="normalizedJournals"
        :items-per-page="itemsPerPage"
        class="elevation-0"
        fixed-header
        height="360"
        show-expand
        v-model:expanded="expanded"
        item-value="journal_doc_id"
      >
        <!-- Journal link -->
        <template #item.j_name="{ item }">
          <a
            class="text-decoration-none"
            :href="journalSearchHref(item.j_name)"
            target="_blank"
            rel="noopener"
            :title="item.j_name"
          >
            {{ item.j_name }}
          </a>
        </template>

        <!-- Mentions badge -->
        <template #item.mentions_count="{ item }">
          <v-chip size="small" variant="flat" class="cm-chip">
            {{ item.mentions_count }}
          </v-chip>
        </template>

        <!-- Expand row content -->
        <template #expanded-row="{ columns, item }">
          <tr>
            <td :colspan="columns.length" class="py-3">
              <div class="cm-subwrap">
                <div class="d-flex align-center justify-content-between mb-2">
                  <div class="small text-muted">
                    Mentions in <strong>{{ item.j_name }}</strong>
                  </div>

                  <div class="d-flex align-center gap-2">
                    <v-progress-circular
                      v-if="mentionsState(item.journal_doc_id).loading"
                      indeterminate
                      size="18"
                      width="2"
                    />
                    <span class="small text-muted" v-else>
                      {{ mentionsState(item.journal_doc_id).total }} total
                    </span>
                  </div>
                </div>

                <v-alert
                  v-if="mentionsState(item.journal_doc_id).error"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mb-2"
                >
                  {{ mentionsState(item.journal_doc_id).error }}
                </v-alert>

                <v-data-table
                  :headers="mentionHeaders"
                  :items="mentionsState(item.journal_doc_id).items"
                  :items-per-page="mentionRows"
                  class="elevation-0 cm-mentions-table"
                  density="compact"
                  fixed-header
                  height="240"
                >
                  <!-- Spot (linked to CM search) -->
                  <template #item.spot="{ item: m }">
                    <a
                      class="text-decoration-none"
                      :href="cmSearchHref(m.spot)"
                      target="_blank"
                      rel="noopener"
                      :title="m.spot"
                    >
                      {{ m.spot }}
                    </a>
                  </template>

                  <!-- Year -->
                  <template #item.year="{ item: m }">
                    <span class="cm-nowrap">{{ m.year || "" }}</span>
                  </template>

                  <!-- Range -->
                  <template #item.range="{ item: m }">
                    <span class="cm-nowrap">
                      <span v-if="m.start !== null && m.start !== undefined">{{ m.start }}</span>
                      <span v-if="m.end !== null && m.end !== undefined">–{{ m.end }}</span>
                    </span>
                  </template>

                  <!-- Fundort / p_link -->
                  <template #item.p_link="{ item: m }">
                    <a
                      v-if="m.p_link !== null && m.p_link !== undefined && String(m.p_link) !== ''"
                      class="text-decoration-none cm-ellipsis"
                      :href="fundSearchHref(m.p_link)"
                      target="_blank"
                      rel="noopener"
                      :title="String(m.p_link)"
                    >
                      {{ m.p_link }}
                    </a>
                    <span v-else class="text-muted">—</span>
                  </template>
                </v-data-table>

                <div class="d-flex justify-content-end mt-2">
                  <v-btn
                    size="small"
                    variant="outlined"
                    :disabled="!canLoadMore(item.journal_doc_id)"
                    @click="loadMore(item.journal_doc_id)"
                  >
                    Load more
                  </v-btn>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "CmEJournalTable",
  props: {
    items: { type: Array, default: () => [] },
    mentionsUrl: { type: String, required: true }, // /jl/cm-e-search/api/journal-mentions/
    cmSearchBase: { type: String, default: "/de/jl/cm-search/?q=" },
    itemsPerPage: { type: Number, default: 10 },

    // paging for mentions
    mentionRows: { type: Number, default: 25 },
  },

  data() {
    return {
      expanded: [],
      mentionsByJournal: {}, // journal_doc_id -> { loading, page, rows, total, items, error }
    };
  },

  computed: {
    headers() {
      return [
        { title: "Journal", key: "j_name", sortable: true },
        { title: "First", key: "first", sortable: true, width: "90px" },
        { title: "Last", key: "last", sortable: true, width: "90px" },
        { title: "Mentions", key: "mentions_count", sortable: true, width: "110px" },
        { title: "", key: "data-table-expand", sortable: false, width: "48px" },
      ];
    },

    mentionHeaders() {
      return [
        { title: "Mention", key: "spot", sortable: true },
        { title: "Year", key: "year", sortable: true, width: "90px" },
        { title: "Range", key: "range", sortable: false, width: "120px" },
        // diese Spalte war bei dir gequetscht -> fixe Breite + ellipsis
        { title: "Fund", key: "p_link", sortable: true, width: "140px" },
      ];
    },

    normalizedJournals() {
      return (this.items || []).map((j) => ({
        journal_doc_id: j.journal_doc_id || j.id || "",
        j_name: j.j_name || "",
        first: j.first ?? null,
        last: j.last ?? null,
        mentions_count: j.mentions_count ?? 0,
      }));
    },
  },

  watch: {
    // hier gehört der "expanded watcher" hin: sobald expand -> Mentions laden
    expanded: {
      handler(newVal) {
        (newVal || []).forEach((journalDocId) => {
          const st = this.mentionsState(journalDocId);
          if (!st.items.length && !st.loading && !st.error) {
            this.fetchMentions(journalDocId, 1, this.mentionRows, /*append*/ false);
          }
        });
      },
      deep: true,
    },
  },

  methods: {
    mentionsState(journalDocId) {
      if (!this.mentionsByJournal[journalDocId]) {
        this.mentionsByJournal[journalDocId] = {
          loading: false,
          page: 1,
          rows: this.mentionRows,
          total: 0,
          items: [],
          error: "",
        };
      }
      return this.mentionsByJournal[journalDocId];
    },

    journalSearchHref(jName) {
      // Journalname direkt als Query
      return `${this.cmSearchBase}${encodeURIComponent(jName || "")}`;
    },

    cmSearchHref(q) {
      return `${this.cmSearchBase}${encodeURIComponent(q || "")}`;
    },

    fundSearchHref(pLink) {
      // falls cm-search advanced queries kann, ist das nützlich:
      // ansonsten ist es zumindest ein klickbarer Wert
      const q = `p_link:${pLink}`;
      return `${this.cmSearchBase}${encodeURIComponent(q)}`;
    },

    canLoadMore(journalDocId) {
      const st = this.mentionsState(journalDocId);
      return !st.loading && st.items.length < (st.total || 0);
    },

    loadMore(journalDocId) {
      const st = this.mentionsState(journalDocId);
      const nextPage = (st.page || 1) + 1;
      this.fetchMentions(journalDocId, nextPage, st.rows, /*append*/ true);
    },

    async fetchMentions(journalDocId, page, rows, append) {
      const st = this.mentionsState(journalDocId);
      st.loading = true;
      st.error = "";

      try {
        const url = new URL(this.mentionsUrl, window.location.origin);
        url.searchParams.set("journal_doc_id", journalDocId);
        url.searchParams.set("page", String(page));
        url.searchParams.set("rows", String(rows));

        const resp = await fetch(url.toString(), {
          headers: { Accept: "application/json" },
        });

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const data = await resp.json();
        const docs = Array.isArray(data.docs) ? data.docs : [];

        const normalized = docs.map((d) => ({
          spot: d.spot || "",
          year: d.year ?? null,
          date: d.date ?? null,
          start: d.start ?? null,
          end: d.end ?? null,
          p_link: d.p_link ?? null,
          p_id: d.p_id ?? null,
        }));

        st.total = Number(data.hits || 0);
        st.page = Number(data.page || page);
        st.rows = Number(data.rows || rows);
        st.items = append ? [...st.items, ...normalized] : normalized;
      } catch (e) {
        st.error = String(e?.message || e);
      } finally {
        st.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cm-chip {
  min-width: 56px;
  justify-content: center;
}
.cm-subwrap {
  border: 1px solid rgba(0,0,0,.12);
  border-radius: 10px;
  padding: 10px;
}
.cm-mentions-table :deep(td),
.cm-mentions-table :deep(th) {
  vertical-align: middle;
}
.cm-nowrap {
  white-space: nowrap;
}
.cm-ellipsis {
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
